import { useDelete, usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import classNames from "classnames";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import type { Agent } from "../../common/types/Agent";
import type { Attachment } from "../../common/types/Attachment";
import type { Conversation } from "../../common/types/Conversation";
import { Mention } from "../../common/types/Mention";
import type { InternalNote, InternalNoteWithMention } from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import { excludeUndefined } from "../../common/utils/array.util";
import { AlbertineServiceAgentId } from "../../common/validate";
import { EventEmitter } from "../../EventEmitter";
import { useClaims } from "../../utils/claims.util";
import { agentNames } from "../../utils/name.util";
import type { Stateful } from "../../utils/react.util";
import { formatChatMessageDate } from "../../utils/time.util";
import { toTimestamp } from "../../utils/timestamp.util";
import { AgentAvatarList } from "../Common/Avatar";
import "./ChatInternalNote.css";
import DeadlineTag from "../Common/DeadlineTag";
import { FlexInline } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import Overlay from "../Common/Overlay";
import RenderLinksWithTooltips from "../Common/RenderLinksWithTooltips";
import ChangeMessageRequest from "./ChangeMessageRequest";
import ChatMessageActions from "./ChatMessageActions";
import ChatMessageInner from "./ChatMessageInner";
import MessageAttachmentPreviews from "./MessageAttachmentPreviews";

export const highlightedCssClassName = "chat-message__is-highlighted";

type Props = {
    idSelector: string;
    internalNote: InternalNote | InternalNoteWithMention;
    conversation: Conversation;
    currentAgent: Agent;
    currentMember: MemberDataFirestoreRecord;
    agents: Agent[];
    editState: Stateful<boolean>;
    notPartOfRequest: boolean;
    currentRequest: Request | undefined;
};

function ChatInternalNote(props: Props) {
    const {
        agents,
        idSelector,
        internalNote,
        conversation,
        currentAgent,
        currentMember,
        editState,
        notPartOfRequest,
        currentRequest,
    } = props;

    const { isAdmin } = useClaims();
    const { mutate: deleteInternalNote } = useDelete("deleteInternalNote");
    const { mutateAsync: completeInternalNote } = usePost("completeInternalNote");
    const { mutateAsync: reopenInternalNote } = usePost("reopenInternalNote");

    const mentionParse = Mention.safeParse(internalNote);
    const mention = mentionParse.success ? mentionParse.data : undefined;

    const attachments: Attachment[] =
        internalNote.attachments ||
        (internalNote.attachment ? [internalNote.attachment] : []);

    const [isCurrentlyEdited, setIsCurrentlyEdited] = editState;
    const [editTopicVisible, setEditTopicVisible] = useState(false);

    const history = useHistory();

    const mentionedAgents = mention?.mentionedAgents
        .map((mentioned) => agents.find((it) => it.id === mentioned))
        .filter(excludeUndefined);
    const dueDate = mention?.mentionDueDate
        ? toTimestamp(mention.mentionDueDate)
        : undefined;
    const resolvedByAgent = mention?.mentionResolvedBy
        ? agents.find((it) => it.id === mention.mentionResolvedBy)
        : undefined;
    const resolvedAt = mention?.mentionResolvedAt
        ? formatChatMessageDate(mention.mentionResolvedAt)
        : undefined;
    const isResolved = resolvedByAgent && resolvedAt;
    const isAutomaticMessage = AlbertineServiceAgentId.safeParse(
        internalNote.sentBy,
    ).success;
    const isCreatorOfMessage = internalNote.sentBy === currentAgent.id;
    const agentIsAllowedToDeleteMessage =
        !isAutomaticMessage && (isCreatorOfMessage || isAdmin);
    const agentIsAllowedToEditMessage =
        !isAutomaticMessage &&
        (isCreatorOfMessage ||
            mention?.mentionedAgents.find(
                (mentionedAgentId) => mentionedAgentId === currentAgent.id,
            ) ||
            isAdmin);
    const agentIsAllowedToCompleteMessage =
        isCreatorOfMessage ||
        mention?.mentionedAgents.find(
            (mentionedAgentId) => mentionedAgentId === currentAgent.id,
        ) ||
        isAdmin;

    const actionOnEdit = () => {
        setIsCurrentlyEdited(true);
    };
    return (
        <div
            id={idSelector}
            className={classNames("chat-message", "chat-internal-note", {
                "chat-internal-note__open-task": mention,
                "chat-internal-note__open-task-open": mention?.mentionStatus === "open",
                "chat-message__is-edited": isCurrentlyEdited,
                "chat-message__not-part-of-topic": notPartOfRequest,
            })}
        >
            <div className="chat-message--spacing">
                <ChatMessageActions
                    currentMember={currentMember}
                    currentAgent={currentAgent}
                    conversation={conversation}
                    messageBase={internalNote}
                    onEdit={agentIsAllowedToEditMessage ? actionOnEdit : undefined}
                    onEditTopic={
                        isAutomaticMessage
                            ? undefined
                            : () => {
                                  setEditTopicVisible(true);
                              }
                    }
                    onDelete={
                        agentIsAllowedToDeleteMessage
                            ? () => {
                                  if (
                                      window.confirm(
                                          `Delete message?\n${internalNote.message}`,
                                      )
                                  ) {
                                      deleteInternalNote({
                                          param: {
                                              conversationId: conversation.id,
                                              noteId: internalNote.id,
                                          },
                                      });
                                  }
                              }
                            : undefined
                    }
                    disabledActions={
                        isResolved
                            ? [
                                  {
                                      dataTooltip: "Reopen to edit note",
                                      action: actionOnEdit,
                                  },
                              ]
                            : []
                    }
                >
                    <ChatMessageInner
                        sender="agent"
                        messageBase={internalNote}
                        onRequestLinkClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            history.push(`/request/${internalNote.request}`);
                        }}
                    >
                        <div className="chat-message__bubble">
                            <MessageAttachmentPreviews
                                currentMember={currentMember}
                                persistedMessageDownload={{
                                    messageId: internalNote.id,
                                }}
                                displayBigImages
                                displayBookingInOverlay
                                attachments={attachments}
                                articles={internalNote.articles}
                                articlesV2={internalNote.articlesV2}
                                bookings={internalNote.bookingSuggestions}
                                displayDivider={internalNote.message.length !== 0}
                                request={internalNote.request || undefined}
                            />

                            {internalNote.message ? (
                                <RenderLinksWithTooltips referenceQuerySelector=".chat">
                                    {internalNote.sentByAgent &&
                                    internalNote.messageHtml ? (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: internalNote.messageHtml,
                                            }}
                                        />
                                    ) : (
                                        internalNote.message
                                    )}
                                </RenderLinksWithTooltips>
                            ) : null}

                            {mentionedAgents && (
                                <div className="chat-internal-note__task-container">
                                    <div className="chat-internal-note__mentions-container">
                                        {isResolved ? (
                                            <FlexInline
                                                className="chat-internal-note__mentions chat-internal-note__task-resolved-by"
                                                gap="small"
                                            >
                                                <PictogramIcon.Check.Small.P90 />
                                                Completed by {resolvedByAgent.name}{" "}
                                                {resolvedAt}
                                            </FlexInline>
                                        ) : (
                                            <span
                                                className="chat-internal-note__mentions web-lib__tooltip web-lib__tooltip--bottom"
                                                data-tooltip={agentNames(
                                                    mentionedAgents,
                                                )}
                                            >
                                                <AgentAvatarList
                                                    agents={mentionedAgents}
                                                    numberOfAvatars={3}
                                                    showNames
                                                />
                                                {dueDate && (
                                                    <DeadlineTag deadlineAt={dueDate} />
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    {mention && (
                                        <button
                                            type="button"
                                            className={classNames(
                                                "chat-internal-note__task-resolve",
                                                {
                                                    "chat-internal-note__task-resolve-done":
                                                        mention.mentionStatus ===
                                                        "resolved",
                                                    "chat-internal-note__task-resolve-disabled":
                                                        !agentIsAllowedToCompleteMessage,
                                                },
                                            )}
                                            disabled={!agentIsAllowedToCompleteMessage}
                                            onClick={async () => {
                                                if (conversation.id && currentAgent) {
                                                    const resolving =
                                                        mention.mentionStatus ===
                                                        "open";
                                                    const param = {
                                                        conversationId: conversation.id,
                                                        noteId: internalNote.id,
                                                    };
                                                    if (resolving) {
                                                        await completeInternalNote({
                                                            param,
                                                        });
                                                    } else {
                                                        await reopenInternalNote({
                                                            param,
                                                        });
                                                    }
                                                    EventEmitter.emit({
                                                        name: "showToastMessage",
                                                        params: {
                                                            message: resolving
                                                                ? "Task completed"
                                                                : "Task re-opened",
                                                            type: "info",
                                                        },
                                                    });
                                                }
                                            }}
                                        >
                                            {mention.mentionStatus === "open" ? (
                                                <>
                                                    <Icon.Check.Small />
                                                    Complete
                                                </>
                                            ) : (
                                                <>
                                                    <Icon.Undo.Small />
                                                    Reopen
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </ChatMessageInner>
                </ChatMessageActions>
            </div>
            {editTopicVisible && (
                <Overlay
                    className="light"
                    onClose={() => {
                        setEditTopicVisible(false);
                        return false;
                    }}
                >
                    <ChangeMessageRequest
                        messages={[internalNote]}
                        conversationId={conversation.id}
                        member={currentMember}
                        currentRequest={currentRequest}
                        close={() => {
                            setEditTopicVisible(false);
                        }}
                    />
                </Overlay>
            )}{" "}
        </div>
    );
}

export default ChatInternalNote;
